import React, { useState, useMemo, useEffect, useRef } from "react";
import { hasStagedChanges } from "../common/stagedChanges";
import { clearStagedDraft, getStagedDraft, setStagedDraft } from "../../utils/stagedDrafts";
import { KeycloakTokenParsed } from "keycloak-js";
import { toast } from 'react-toastify';

import { createProjectMeetingNote, updateProjectMeetingNotes, deleteProjectMeetingNotes, getProjectMeetingNoteTypes, getProjectMeetingNotes as fetchProjectMeetingNotes } from "../../api/meetingNotes";
import { deleteMeetingNoteDocument } from "../../api/documents";
import { useProjectMeetingNotesStore, useProjectMeetingNotesPage } from "../../state/projects";
import { useUsersStore, useUsersPage } from "../../state/users";
import { MeetingNotesDocumentAttachment } from './MeetingNotesDocumentAttachment';
import { AddMeetingNoteModal } from './AddMeetingNoteModal';
import { MeetingTypesManager } from './MeetingTypesManager';
import { AttendeeSelectionModal } from './AttendeeSelectionModal';
import { AttendeeSelectionModalWithExternal } from './AttendeeSelectionModalWithExternal';
import { LoadingSpinner } from '../LoadingSpinner';

import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCalendarAlt,
  FaFileAlt,
  FaTrash
} from 'react-icons/fa';

import { 
  getTableHeaderStyle, 
  getFormLabelStyle, 
  getMultilineFieldStyle,
  getFormSelectStyle, 
  getFormSelectContainerStyle,
  DesignSystem
} from '../../styles/designSystem';

interface TabMeetingNotesProps {
  projectId: string | undefined;
  token: string | undefined;
  projectData: ProjectInterface,
  tokenParsed: KeycloakTokenParsed | undefined;
  onProjectUpdate?: () => Promise<void>;
  readOnly?: boolean;
  highlightedItemId?: string | null;
  projectUsers?: ProjectUserWithRole[];
  archived?: boolean;
  archivedMeetingNotes?: ProjectMeetingNotesInterface;
}

// Meeting Note Row Component
const MeetingNoteRow = ({
  meetingNote,
  projectData,
  projectUsers,
  allUsers,
  onSubmit,
  onDelete,
  meetingTypes,
  onMeetingNoteUpdate,
  isNew = false,
  isHighlighted = false,
  readOnly = false
}: {
  meetingNote: ProjectMeetingNote,
  projectData: ProjectInterface,
  projectUsers?: ProjectUserWithRole[],
  allUsers?: ProjectUserInterface[],
  onSubmit: Function,
  onDelete: (meetingNoteId: string) => void,
  meetingTypes: ProjectMeetingNoteTypesInterface[],
  onMeetingNoteUpdate?: () => Promise<void>,
  isNew?: boolean,
  isHighlighted?: boolean,
  readOnly?: boolean
}) => {
  // Normalize meeting note data - keep vendors as-is (can be UUIDs or external user objects)
  const normalizeAttendees = (meetingNote: ProjectMeetingNote) => {
    const normalized = { ...meetingNote };
    
    // Normalize customers - convert objects to UUID strings
    if (normalized.customers) {
      normalized.customers = normalized.customers.map((customer: any) => {
        if (typeof customer === 'string') return customer;
        return customer.id || customer.user_id || customer;
      });
    }
    
    // Keep vendors as-is - they can be UUID strings or external user objects
    if (normalized.vendors) {
      normalized.vendors = normalized.vendors.map((vendor: any) => {
        // If it's already a string (UUID or name), keep it
        if (typeof vendor === 'string') return vendor;
        // If it's an external user object, keep it as object
        if (vendor.first_name && vendor.last_name && !vendor.id && !vendor.user_id) {
          return {
            first_name: vendor.first_name,
            last_name: vendor.last_name,
            ...(vendor.email && { email: vendor.email })
          };
        }
        // Otherwise try to extract UUID
        return vendor.id || vendor.user_id || vendor;
      });
    }
    
    return normalized;
  };

  // Unsaved edits survive a tab switch, which unmounts the row; see utils/stagedDrafts.
  const draftKey = `meeting-note:${meetingNote.id}`;
  const [form, setForm] = useState<ReturnType<typeof normalizeAttendees>>(
    () => getStagedDraft<ReturnType<typeof normalizeAttendees>>(draftKey) ?? normalizeAttendees(meetingNote)
  );
  // Last saved copy of the note. Edits are staged in `form` until Save; Cancel restores this copy.
  const [savedForm, setSavedForm] = useState(normalizeAttendees(meetingNote));
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [meetingTypeError, setMeetingTypeError] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const rowRef = React.useRef<HTMLDivElement>(null);

  // A reload of this note must not discard edits the user has staged.
  const savedFormRef = useRef(savedForm);
  savedFormRef.current = savedForm;
  useEffect(() => {
    setSavedForm(normalizeAttendees(meetingNote));
    setForm(prev => (hasStagedChanges(prev, savedFormRef.current) ? prev : normalizeAttendees(meetingNote)));
  }, [meetingNote]);

  // Scroll to highlighted item
  useEffect(() => {
    if (isHighlighted && rowRef.current) {
      setTimeout(() => {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isHighlighted]);

  // Function to save changes
  const saveChanges = async (updatedForm: any) => {
    setIsSaving(true);
    
    // Normalize data before saving - customers are UUID strings, vendors can be UUIDs or external user objects
    const normalizedData = {
      ...updatedForm,
      customers: updatedForm.customers?.map((customer: any) => {
        if (typeof customer === 'string') return customer;
        return customer.id || customer.user_id || customer;
      }) || [],
      vendors: updatedForm.vendors?.map((vendor: any) => {
        // Keep as-is if it's a string (UUID or name)
        if (typeof vendor === 'string') return vendor;
        // Keep as object if it's an external user
        if (vendor.first_name && vendor.last_name && !vendor.id && !vendor.user_id) {
          return {
            first_name: vendor.first_name,
            last_name: vendor.last_name,
            ...(vendor.email && { email: vendor.email })
          };
        }
        // Otherwise extract UUID
        return vendor.id || vendor.user_id || vendor;
      }) || []
    };
    
    console.log('Meeting Note: Saving changes:', normalizedData);
    try {
      await onSubmit(normalizedData);
      setSavedForm({ ...updatedForm });
      console.log('Meeting Note: Successfully saved changes');
    } catch (error) {
      console.error('Error saving meeting note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    if (field === 'meeting_type_id' && !value) {
      setMeetingTypeError(true);
      toast.error('Meeting Type is required');
      return;
    }
    if (field === 'meeting_type_id') setMeetingTypeError(false);

    // Stages the change; nothing is sent until the user presses Save.
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Blur stages the value; a blur without a change does not send a request.
  const handleFieldBlur = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Any staged difference from the saved copy means unsaved changes.
  const isDirty = hasStagedChanges(form, savedForm);

  const saveStagedChanges = () => {
    if (!form.meeting_type_id) {
      setMeetingTypeError(true);
      toast.error('Meeting Type is required');
      return;
    }
    saveChanges(form);
  };

  const discardStagedChanges = () => {
    setForm({ ...savedForm });
    setMeetingTypeError(false);
  };

  // Keep the draft in step with the row: stored while edits are unsaved, cleared once they are saved or cancelled.
  useEffect(() => {
    if (isDirty) setStagedDraft(draftKey, form);
    else clearStagedDraft(draftKey);
  }, [isDirty, form, draftKey]);

  const handleMeetingNoteUpdate = async () => {
    // Reload meeting notes to show updated document
    if (onMeetingNoteUpdate) {
      await onMeetingNoteUpdate();
    }
  };

  // Get attendee names for display
  const getAttendeeName = (userId: string | any) => {
    // Handle external user objects
    if (typeof userId === 'object' && userId !== null) {
      // Check if it's an external user (has first_name and last_name but no id/user_id)
      if (userId.first_name && userId.last_name && !userId.id && !userId.user_id) {
        // If first_name and last_name are the same (single word input), show only once
        if (userId.first_name === userId.last_name) {
          return userId.first_name;
        }
        return `${userId.first_name} ${userId.last_name}`;
      }
      // Otherwise try to extract UUID
      userId = userId.id || userId.user_id;
    }
    
    // Handle string UUIDs or names
    const user = projectUsers?.find(
      u => u.user_id === userId || u.user?.id === userId || u.id === userId
    );
    if (user) {
      return `${user.user?.first_name} ${user.user?.last_name}`;
    }

    // Fall back to the global users list - the attendee may no longer be a project member
    const globalUser = allUsers?.find(u => u.id === userId);
    if (globalUser) {
      return `${globalUser.first_name} ${globalUser.last_name}`;
    }

    return 'Unknown User';
  };

  const handleCustomerSelection = (selectedUsers: string[]) => {
    handleFieldChange('customers', selectedUsers);
  };

  const handleVendorSelection = (selectedUsers: (string | { first_name: string; last_name: string; email?: string })[]) => {
    handleFieldChange('vendors', selectedUsers);
  };

  // Get meeting type name
  const getMeetingTypeName = (typeId: string) => {
    const type = meetingTypes.find(t => t.id === typeId);
    return type?.name || 'No Type';
  };

  return (
    <div 
      ref={rowRef}
      data-testid="meeting-note-row"
      data-item-id={meetingNote.id}
      className={`group hover:bg-[#fafafa]/50 transition-all duration-200 ${
        isNew ? 'bg-[#f5f5f5]/30' : ''
      } ${
        isHighlighted ? 'bg-yellow-50/50 border-l-4 border-yellow-400' : ''
      } ${
        isDirty ? 'shadow-[inset_3px_0_0_#f59e0b]' : ''
      }`}
    >
      {isDirty && (
        <div data-testid="meeting-note-unsaved-bar" className="flex items-center justify-end gap-2 px-6 py-1.5 bg-amber-50 border-b border-amber-100">
          <span className="text-[11px] text-amber-700 mr-auto">Unsaved changes</span>
          <button
            data-testid="meeting-note-cancel"
            onClick={(e) => { e.stopPropagation(); discardStagedChanges(); }}
            disabled={isSaving}
            className="px-2 py-1 text-xs font-medium text-[#444] bg-white border border-[#eaeaea] rounded hover:bg-[#fafafa] transition-colors duration-150 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="meeting-note-save"
            onClick={(e) => { e.stopPropagation(); saveStagedChanges(); }}
            disabled={isSaving}
            className="px-2 py-1 text-xs font-medium text-white bg-[#171717] rounded hover:bg-[#333] transition-colors duration-150 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
      {/* Main Row */}
      <div className="flex items-start px-6 py-4 relative" style={{ borderBottom: '0.5px solid #eaeaea' }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-[#fafafa] transition-colors relative z-10"
        >
          <FaChevronDown className={`w-3 h-3 text-[#aaa] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Date */}
        <div className="mr-6 w-32 relative z-10 flex items-center pt-1">
          <div className="relative mr-2">
            {/* Rotating Ring around calendar icon - when saving any field */}
            {isSaving && (
              <div className="absolute inset-0 w-5 h-5 -ml-1 -mt-1 rounded-full border-2 border-transparent animate-spin border-t-indigo-600/60 border-r-indigo-600/60" style={{ animation: 'spin 1s linear infinite' }} />
            )}
            <FaCalendarAlt className="w-3 h-3 text-[#aaa]" />
          </div>
          <span className="text-sm text-[#444]">
            {form.date ? (() => {
              // Parse date without timezone conversion to avoid shifting
              const dateStr = form.date.includes('T') ? form.date.split('T')[0] : form.date;
              const [year, month, day] = dateStr.split('-');
              const formattedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
              console.log('Displaying date:', form.date, '->', formattedDate);
              return formattedDate;
            })() : 'No Date'}
          </span>
        </div>

        {/* Meeting Type */}
        <div className="mr-6 w-40 relative z-10 pt-1">
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-[#f5f5f5] text-[#555] rounded-md">
            {getMeetingTypeName(form.meeting_type_id)}
          </span>
        </div>

        {/* Notes Preview */}
        <div className="flex-1 min-w-0 relative z-10 flex items-start">
          <textarea
            value={form.notes || ''}
            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
            onBlur={(e) => handleFieldBlur('notes', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-[#aaa] resize-none"
            style={getMultilineFieldStyle()}
            placeholder="Meeting notes..."
            rows={5}
            maxLength={12000}
          />
        </div>

        {/* Customer Attendee Preview */}
        <div className="mx-6 w-40 relative z-10 flex items-center space-x-1 pt-1">
          {form.customers && form.customers.length > 0 ? (
            <>
              <div className="w-5 h-5 bg-[#f5f5f5] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-[#171717]">C</span>
              </div>
              <span className="text-xs text-[#555] truncate">
                {form.customers.length === 1 
                  ? getAttendeeName(form.customers[0])
                  : `${form.customers.length} customers`
                }
              </span>
            </>
          ) : (
            <>
              <div className="w-5 h-5 bg-[#f5f5f5] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-[#aaa]">C</span>
              </div>
              <span className="text-xs text-[#aaa]">No customers</span>
            </>
          )}
        </div>

        {/* Vendor Attendee Preview */}
        <div className="mx-6 w-40 relative z-10 flex items-center space-x-1 pt-1">
          {form.vendors && form.vendors.length > 0 ? (
            <>
              <div className="w-5 h-5 bg-[#f5f5f5] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-[#171717]">V</span>
              </div>
              <span className="text-xs text-[#555] truncate">
                {form.vendors.length === 1 
                  ? getAttendeeName(form.vendors[0])
                  : `${form.vendors.length} vendors`
                }
              </span>
            </>
          ) : (
            <>
              <div className="w-5 h-5 bg-[#f5f5f5] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-[#aaa]">V</span>
              </div>
              <span className="text-xs text-[#aaa]">No vendors</span>
            </>
          )}
        </div>

        {/* Documents */}
        <div className="ml-6 w-32 relative z-10 pt-1">
          <div onClick={(e) => e.stopPropagation()}>
            <MeetingNotesDocumentAttachment 
              projectId={projectData?.id || ''} 
              recordId={meetingNote.id}
              documentUrl={form.document || undefined}
              fileName={form.file_name || undefined}
              onUpdate={handleMeetingNoteUpdate}
              onDocumentChange={(docUrl, docName) => {
                // The document endpoint already stores the link, so no update request is needed.
                const documentFields = { document: docUrl || null, file_name: docName || null };
                setForm(prev => ({ ...prev, ...documentFields }));
                setSavedForm(prev => ({ ...prev, ...documentFields }));
              }}
            />
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2" style={{ 
          backgroundColor: DesignSystem.colors.background.gray50,
          borderTop: `0.5px solid ${DesignSystem.colors.border.gray200}` 
        }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Meeting Notes and Details */}
            <div className="lg:col-span-2 space-y-4">
              {/* Meeting Notes */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Meeting Notes</label>
                <textarea
                  value={form.notes || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  className={`w-full text-sm text-[#444] bg-white border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 resize-none ${
                    (form.notes || '') !== (meetingNote.notes || '')
                      ? 'border-amber-400 focus:ring-amber-200 focus:border-amber-500'
                      : 'border-[#eaeaea] focus:ring-0 focus:border-[#888]'
                  }`}
                  placeholder="Add meeting notes, discussions, decisions, and action items..."
                  rows={6}
                  maxLength={12000}
                  style={{
                    fontFamily: DesignSystem.typography.body.fontFamily,
                    fontSize: DesignSystem.typography.body.fontSize
                  }}
                />
                <div className={`mt-1 text-right text-xs ${(form.notes || '').length >= 12000 ? 'text-red-500' : 'text-[#888]'}`}>
                  {(form.notes || '').length.toLocaleString()} / {(12000).toLocaleString()}
                </div>
                {(form.notes || '') !== (meetingNote.notes || '') && (
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      onClick={() => handleFieldBlur('notes', form.notes)}
                      disabled={isSaving}
                      className="px-3 py-1 text-xs font-medium text-white bg-[#171717] rounded-md hover:bg-[#333] transition-colors disabled:opacity-50"
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setForm(prev => ({ ...prev, notes: meetingNote.notes || '' }))}
                      disabled={isSaving}
                      className="px-3 py-1 text-xs font-medium text-[#444] bg-white border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] transition-colors disabled:opacity-50"
                    >
                      Discard
                    </button>
                    <span className="text-[11px] text-amber-600">Unsaved changes</span>
                  </div>
                )}
              </div>
            </div>

            {/* Meeting Details and Attendees */}
            <div className="space-y-4">
              
              {/* Meeting Date */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Meeting Date</label>
                <div style={getFormSelectContainerStyle()}>
                  <input
                    type="date"
                    value={(() => {
                      if (!form.date) return '';
                      // Extract date part without timezone conversion
                      const dateStr = form.date.includes('T') ? form.date.split('T')[0] : form.date;
                      console.log('Date display:', form.date, '->', dateStr);
                      return dateStr;
                    })()}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) {
                        // Store as simple date string, let API handle timezone
                        const isoValue = value + 'T00:00:00.000Z';
                        console.log('Date change:', value, '->', isoValue);
                        handleFieldChange('date', isoValue);
                      } else {
                        handleFieldChange('date', '');
                      }
                    }}
                    className="w-full bg-transparent border-0 focus:outline-none"
                    style={{ fontSize: '14px' }}
                  />
                </div>
              </div>

              {/* Meeting Type */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Meeting Type</label>
                <div
                  style={{
                    ...getFormSelectContainerStyle(),
                    ...(meetingTypeError ? { border: '1px solid #ef4444' } : {}),
                  }}
                >
                  <select
                    value={form.meeting_type_id || ''}
                    onChange={(e) => handleFieldChange('meeting_type_id', e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="">Select meeting type</option>
                    {meetingTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Customer Attendees */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Customer Attendees (up to 10)</label>
                
                {/* Selected Attendees Display */}
                {form.customers && form.customers.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {form.customers.map((userId) => (
                      <div
                        key={userId}
                        className="inline-flex items-center space-x-2 bg-[#f5f5f5] text-[#555] px-3 py-1 rounded-full text-sm"
                      >
                        <span>{getAttendeeName(userId)}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const updatedCustomers = form.customers?.filter(id => id !== userId) || [];
                            handleFieldChange('customers', updatedCustomers);
                          }}
                          className="text-[#171717] hover:text-[#555] ml-1"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Button */}
                <button
                  onClick={() => setShowCustomerModal(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-[#555] bg-[#fafafa] border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] hover:border-[#eaeaea] transition-colors"
                >
                  {form.customers && form.customers.length > 0 
                    ? `Edit Customer Attendees (${form.customers.length}/10)`
                    : 'Add Customer Attendees'
                  }
                </button>
              </div>

              {/* Vendor Attendees */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Vendor Attendees (up to 10)</label>
                
                {/* Selected Attendees Display */}
                {form.vendors && form.vendors.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {form.vendors.map((userId, index) => (
                      <div
                        key={typeof userId === 'string' ? userId : `external-${index}`}
                        className="inline-flex items-center space-x-2 bg-[#f5f5f5] text-[#555] px-3 py-1 rounded-full text-sm"
                      >
                        <span>{getAttendeeName(userId)}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const updatedVendors = form.vendors?.filter((_, i) => i !== index) || [];
                            handleFieldChange('vendors', updatedVendors);
                          }}
                          className="text-[#171717] hover:text-[#555] ml-1"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Button */}
                <button
                  onClick={() => setShowVendorModal(true)}
                  className="w-full px-4 py-2 text-sm font-medium text-[#555] bg-[#fafafa] border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] hover:border-[#eaeaea] transition-colors"
                >
                  {form.vendors && form.vendors.length > 0 
                    ? `Edit Vendor Attendees (${form.vendors.length}/10)`
                    : 'Add Vendor Attendees'
                  }
                </button>
              </div>

              {/* Actions */}
              <div className="pt-4">
                <label className="block mb-2" style={getFormLabelStyle()}>Actions</label>
                {!readOnly && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this meeting note?')) {
                      onDelete(meetingNote.id);
                    }
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete Note</span>
                </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendee Selection Modals */}
      <AttendeeSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        title="Select Customer Attendees"
        users={projectUsers || []}
        selectedUsers={form.customers || []}
        maxUsers={10}
        onConfirm={handleCustomerSelection}
      />

      <AttendeeSelectionModalWithExternal
        isOpen={showVendorModal}
        onClose={() => setShowVendorModal(false)}
        title="Select Vendor Attendees"
        users={projectUsers || []}
        selectedUsers={form.vendors || []}
        maxUsers={10}
        onConfirm={handleVendorSelection}
        allowExternal={true}
      />
    </div>
  );
};

export const TabMeetingNotes = (
  { token, projectData, projectId, onProjectUpdate, highlightedItemId, projectUsers, readOnly, archived, archivedMeetingNotes }: TabMeetingNotesProps
) => {
  // Add cleanup flag to prevent DOM operations after unmount. Must be a ref,
  // not state: under React StrictMode's dev-only mount/cleanup/remount
  // simulation, a state-based flag set `true` by the cleanup never gets reset
  // back to `false`, permanently disabling every effect gated on it.
  const isUnmountedRef = useRef(false);
  const [newRows, setNewRows] = useState<ProjectMeetingNote[]>([]);
  const [showAddMeetingNoteModal, setShowAddMeetingNoteModal] = useState(false);
  const [showMeetingTypesManager, setShowMeetingTypesManager] = useState(false);
  const [refreshMeetingTypes, setRefreshMeetingTypes] = useState(0);
  const [meetingTypes, setMeetingTypes] = useState<ProjectMeetingNoteTypesInterface[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { users: allUsers } = useUsersStore();
  const { loadUsersPage } = useUsersPage();

  useEffect(() => {
    if (allUsers.length === 0) {
      loadUsersPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const lastMeetingTypesLoadRef = useRef<number>(0);

  // Load meeting notes
  const { projectMeetingNotes } = useProjectMeetingNotesStore();
  const projectMeetingNotesForProject = projectMeetingNotes[projectId || ''] || [];
  const getProjectMeetingNotes = useProjectMeetingNotesPage(projectId || '');

  // Guard against React StrictMode's dev-only double-invoke firing this load
  // twice on mount, while still reloading on a genuine projectId change (this
  // component does not remount when switching projects while already on the
  // Meeting Notes tab).
  const lastLoadedProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!projectId || lastLoadedProjectIdRef.current === projectId || archived) return;
    lastLoadedProjectIdRef.current = projectId;
    setIsLoading(true);
    getProjectMeetingNotes.loadProjectMeetingNotesPage(1).then(() => setIsLoading(false)).catch(() => setIsLoading(false));
  }, [projectId, archived]);

  // Load archived meeting notes when in archived mode
  useEffect(() => {
    if (!archived || !projectId || !token) return;

    // If archived data is passed as prop, use it directly
    if (archivedMeetingNotes) {
      const store = useProjectMeetingNotesStore.getState();
      store.setProjectMeetingNotesPage(projectId, archivedMeetingNotes);
      setIsLoading(false);
      return;
    }

    const loadArchivedMeetingNotes = async () => {
      try {
        const data = await fetchProjectMeetingNotes(token, projectId, { archived: true });
        // Update the store with archived data
        const store = useProjectMeetingNotesStore.getState();
        store.setProjectMeetingNotesPage(projectId, data);
      } catch (error) {
        console.error('Failed to load archived meeting notes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadArchivedMeetingNotes();
  }, [projectId, token, archived, archivedMeetingNotes]);

  // Listen for document deletion events from Documents tab
  useEffect(() => {
    console.log('🎧 [TabMeetingNotes] Setting up event listener for meetingNoteDocumentDeleted');
    console.log('🎧 [TabMeetingNotes] Current projectId:', projectId);
    
    const handleDocumentDeleted = (event: CustomEvent) => {
      console.log('📢 [TabMeetingNotes] Received meetingNoteDocumentDeleted event');
      console.log('📢 [TabMeetingNotes] Event detail:', event.detail);
      console.log('📢 [TabMeetingNotes] Current projectId:', projectId);
      console.log('📢 [TabMeetingNotes] Event projectId:', event.detail.projectId);
      
      if (projectId && event.detail.projectId === projectId) {
        console.log('🔄 [TabMeetingNotes] Project IDs match, will refresh after delay');
        // Add delay to allow backend to process the deletion and update relationships
        setTimeout(() => {
          console.log('🔄 [TabMeetingNotes] Refreshing meeting notes now');
          getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
          console.log('✅ [TabMeetingNotes] Refresh triggered');
        }, 2000); // 2 second delay
      } else {
        console.log('⚠️ [TabMeetingNotes] Project IDs do not match or projectId is undefined');
      }
    };

    window.addEventListener('meetingNoteDocumentDeleted', handleDocumentDeleted as EventListener);
    console.log('✅ [TabMeetingNotes] Event listener added');
    
    return () => {
      console.log('🧹 [TabMeetingNotes] Removing event listener');
      window.removeEventListener('meetingNoteDocumentDeleted', handleDocumentDeleted as EventListener);
    };
  }, [projectId, getProjectMeetingNotes]);

  // Load meeting types for filter
  useEffect(() => {
    const loadMeetingTypes = async () => {
      if (!token) return;

      try {
        const response = await getProjectMeetingNoteTypes(token);
        setMeetingTypes(response.results);
      } catch (error) {
        console.error('Failed to load meeting types:', error);
      }
    };

    // Guard against StrictMode double-invoke only when refreshMeetingTypes hasn't changed
    if (lastMeetingTypesLoadRef.current === refreshMeetingTypes) return;
    lastMeetingTypesLoadRef.current = refreshMeetingTypes;

    loadMeetingTypes();
  }, [token, refreshMeetingTypes]); // Reload when types are updated

  // Filter meeting notes
  const filteredMeetingNotes = useMemo(() => {
    let filtered = [...projectMeetingNotesForProject, ...newRows];

    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(note => 
        note.notes?.toLowerCase().includes(searchLower)
      );
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(note => {
        if (!note.date) return false;
        const noteDate = new Date(note.date);
        
        switch (dateFilter) {
          case 'today':
            return noteDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return noteDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return noteDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(note => note.meeting_type_id === typeFilter);
    }

    return filtered;
  }, [projectMeetingNotesForProject, newRows, searchQuery, dateFilter, typeFilter]);

  const updateProjectMeetingNoteHandler = async (form: ProjectMeetingNote) => {
    if (token && projectId) {
      try {
        await updateProjectMeetingNotes(token, projectId, form.id, form);
        // Only reload meeting notes list, don't reload entire project for auto-save
        await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
        // Note: onProjectUpdate() removed from auto-save to prevent data loss
        // Only call it for major changes, not field-level saves
      } catch (error) {
        console.error('Failed to update meeting note:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to update meeting note');
        throw error;
      }
    }
  };

  const handleMeetingNoteDocumentUpdate = async () => {
    // Reload meeting notes to show updated document
    await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
  };

  const createProjectMeetingNoteHandler = async (idx: number, form: ProjectMeetingNote) => {
    if (token && projectId) {
      try {
        await createProjectMeetingNote(token, projectId, form);
        await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
        setNewRows(prev => prev.filter((_, index) => index !== idx));
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      } catch (error) {
        console.error('Failed to add meeting note:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to add meeting note');
        throw error;
      }
    }
  };

  const handleAddMeetingNote = async (itemData: any) => {
    try {
      if (token && projectId) {
        await createProjectMeetingNote(token, projectId, {
          ...itemData,
          id: crypto.randomUUID(),
          project_id: projectId,
        });
        await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
        toast.success('Meeting note added successfully');
      }
    } catch (error) {
      console.error('Failed to add meeting note:', error);
      toast.error('Failed to add meeting note');
      throw error;
    }
  };

  const deleteMeetingNoteHandler = async (meetingNoteId: string) => {
    if (!token || !projectId) return;

    try {
      // Find the meeting note to check if it has a document
      const meetingNote = projectMeetingNotesForProject.find((item: ProjectMeetingNote) => item.id === meetingNoteId);
      
      // If meeting note has a document, delete it first
      if (meetingNote?.document) {
        console.log(`Deleting document for meeting note ${meetingNoteId} before deleting the record itself`);
        try {
          await deleteMeetingNoteDocument(token, projectId, meetingNoteId);
          console.log('Document deleted successfully');
        } catch (docError) {
          console.error('Failed to delete meeting note document:', docError);
          // Continue with meeting note deletion even if document deletion fails
          toast.warning('Meeting note document could not be deleted, but will delete the record');
        }
      }
      
      // Delete the meeting note itself
      await deleteProjectMeetingNotes(token, projectId, meetingNoteId);
      await getProjectMeetingNotes.loadProjectMeetingNotesPage(1);
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
      toast.success('Meeting note deleted successfully');
    } catch (error) {
      console.error('Error deleting meeting note:', error);
      toast.error('Failed to delete meeting note');
    }
  };

  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll detection for shadow effect
  useEffect(() => {
    if (isUnmountedRef.current) return;

    const handleScroll = () => {
      if (!isUnmountedRef.current) {
      setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup effect to prevent DOM operations after unmount. Reset on setup
  // too, so StrictMode's mount/cleanup/remount simulation doesn't leave this
  // permanently `true` after the initial mount.
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  if (isLoading && projectMeetingNotesForProject.length === 0) {
    return <LoadingSpinner text="Loading meeting notes..." />;
  }

  return (
    <div className="min-h-screen">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow duration-200 ${isScrolled ? 'shadow-[0_4px_12px_rgba(0,0,0,0.06)]' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #eaeaea' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#aaa] w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search meeting notes..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 w-80 text-[13px] text-[#444] bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 focus:bg-white transition-all duration-150 placeholder-[#aaa]"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-[#888]">
                      {filteredMeetingNotes.length} of {projectMeetingNotesForProject.length} notes
                    </span>
                    <div className="w-px h-4 bg-[#ddd]"></div>
                    <span className="text-[#888]">
                      {filteredMeetingNotes.filter(note => note.date && new Date(note.date) >= new Date(Date.now() - 7*24*60*60*1000)).length} this week
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Filters */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      showFilters 
                        ? 'bg-[#171717] text-white border border-[#171717]'
                        : 'bg-white text-[#444] hover:bg-[#fafafa] border border-[#eaeaea]'
                    }`}
                  >
                    <FaFilter className="w-3.5 h-3.5" />
                    <span>Filter</span>
                    {(dateFilter !== 'all' || typeFilter !== 'all') && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white text-[#171717] rounded-full font-semibold">
                        {[dateFilter, typeFilter].filter(f => f !== 'all').length}
                      </span>
                    )}
                  </button>

                  {/* Manage Meeting Types */}
                  {!readOnly && (
                  <button
                    onClick={() => setShowMeetingTypesManager(true)}
                    className="flex items-center space-x-2 bg-white text-[#888] border border-[#eaeaea] px-3.5 py-2 rounded-lg text-[13px] font-medium hover:bg-[#fafafa] hover:border-[#ddd] transition-colors duration-150"
                  >
                    <FaCalendarAlt className="w-3.5 h-3.5" />
                    <span>Manage Types</span>
                  </button>
                  )}

                  {/* Add Meeting Note */}
                  {!readOnly && (
                  <button
                    onClick={() => setShowAddMeetingNoteModal(true)}
                    className="flex items-center space-x-2 bg-[#171717] text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333] transition-colors duration-150"
                  >
                    <FaPlus className="w-3.5 h-3.5" />
                    <span>Add Meeting Note</span>
                  </button>
                  )}
                </div>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #eaeaea' }}>
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-3">
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Dates</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Types</option>
                        {meetingTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Active Filters & Clear */}
                  {(dateFilter !== 'all' || typeFilter !== 'all') && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-[#888]">Active filters:</span>
                        {dateFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#171717] rounded-md">
                            Date: {dateFilter}
                            <button onClick={() => setDateFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                        {typeFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#171717] rounded-md">
                            Type: {meetingTypes.find(t => t.id === typeFilter)?.name || typeFilter}
                            <button onClick={() => setTypeFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setDateFilter('all');
                          setTypeFilter('all');
                        }}
                        className="text-xs text-[#888] hover:text-[#444] font-medium"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="w-8"></div> {/* Expand button space */}
                <div className="mr-6 w-32" role="columnheader">Date</div>
                <div className="mr-6 w-40" role="columnheader">Meeting Type</div>
                <div className="flex-1 min-w-0" role="columnheader">Notes</div>
                <div className="mx-6 w-40" role="columnheader">Customer</div>
                <div className="mx-6 w-40" role="columnheader">Vendor</div>
                <div className="ml-6 w-32" role="columnheader">Documents</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Meeting Notes List */}
      <div className="max-w-[1280px] mx-auto">
        {filteredMeetingNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#888]">
            <FaFileAlt className="w-12 h-12 mb-4 text-[#ccc]" />
            <h3 className="text-lg font-medium mb-2">No meeting notes found</h3>
            <p className="text-sm text-center max-w-md">
              {searchQuery || dateFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by adding your first meeting note'
              }
            </p>
          </div>
        ) : (
          <div>
            {/* New Rows */}
            {newRows.map((meetingNote, index) => (
              <MeetingNoteRow
                key={`new-${index}`}
                meetingNote={meetingNote}
                projectData={projectData}
                projectUsers={projectUsers}
                allUsers={allUsers}
                onSubmit={(form: ProjectMeetingNote) => createProjectMeetingNoteHandler(index, form)}
                onDelete={deleteMeetingNoteHandler}
                meetingTypes={meetingTypes}
                onMeetingNoteUpdate={handleMeetingNoteDocumentUpdate}
                isNew={true}
                isHighlighted={highlightedItemId === meetingNote.id}
                readOnly={readOnly}
              />
            ))}
            
            {/* Existing Rows */}
            {filteredMeetingNotes.map((meetingNote) => (
              <MeetingNoteRow
                key={meetingNote.id}
                meetingNote={meetingNote}
                projectData={projectData}
                projectUsers={projectUsers}
                allUsers={allUsers}
                onSubmit={updateProjectMeetingNoteHandler}
                onDelete={deleteMeetingNoteHandler}
                meetingTypes={meetingTypes}
                onMeetingNoteUpdate={handleMeetingNoteDocumentUpdate}
                isHighlighted={highlightedItemId === meetingNote.id}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Meeting Note Modal */}
      <AddMeetingNoteModal
        isOpen={showAddMeetingNoteModal}
        onClose={() => {
          setShowAddMeetingNoteModal(false);
          setRefreshMeetingTypes(prev => prev + 1); // Refresh types in case new ones were created
        }}
        onAddItem={handleAddMeetingNote}
        projectData={projectData}
        projectUsers={projectUsers}
        allUsers={allUsers}
        token={token}
      />

      {/* Meeting Types Manager Modal */}
      {!readOnly && showMeetingTypesManager && (
        <MeetingTypesManager
          token={token}
          onClose={() => {
            setShowMeetingTypesManager(false);
            setRefreshMeetingTypes(prev => prev + 1); // Trigger refresh
          }}
          isModal={true}
        />
      )}
    </div>
  );
}; 